// Redaction applied at capture time, before anything is stored or sent.
// By default only credential headers are redacted (test sites, site owners
// reporting). Everything else is opt-in per site through config.

import { CREDENTIAL_HEADERS } from './config.js';

export const REDACTED = '[redacted]';

const lower = (s) => String(s).toLowerCase();

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function redactValue(value, keys) {
  if (Array.isArray(value)) return value.map((item) => redactValue(item, keys));
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = keys.has(lower(k)) ? REDACTED : redactValue(v, keys);
    }
    return out;
  }
  return value;
}

/**
 * @param {{ redactHeaders: string[], redactKeys: string[], redactParams: string[] }} config
 */
export function createRedactor(config) {
  const headerNames = new Set([...CREDENTIAL_HEADERS, ...config.redactHeaders.map(lower)]);
  const bodyKeys = new Set(config.redactKeys.map(lower));
  const params = new Set(config.redactParams.map(lower));
  const keyPattern = bodyKeys.size ? [...bodyKeys].map(escapeRegExp).join('|') : '';
  // "key": "value" or "key": 123 (also matches a value cut off by truncation)
  const jsonPattern = keyPattern
    ? new RegExp(`("(?:${keyPattern})"\\s*:\\s*)("(?:[^"\\\\]|\\\\.)*"?|[^,}\\]\\s]+)`, 'gi')
    : null;
  // key=value in URL-encoded bodies
  const formPattern = keyPattern ? new RegExp(`(^|&)(${keyPattern})=[^&]*`, 'gi') : null;

  return {
    /** @param {Record<string, string>} headers */
    headers(headers) {
      const out = {};
      for (const [name, value] of Object.entries(headers || {})) {
        out[name] = headerNames.has(lower(name)) ? REDACTED : value;
      }
      return out;
    },

    /** @param {string} url */
    url(url) {
      if (!params.size || !url) return url;
      try {
        const parsed = new URL(url, globalThis.location ? globalThis.location.href : undefined);
        let changed = false;
        for (const key of [...parsed.searchParams.keys()]) {
          if (params.has(lower(key))) {
            parsed.searchParams.set(key, REDACTED);
            changed = true;
          }
        }
        return changed ? parsed.href : url;
      } catch {
        return url;
      }
    },

    /**
     * Redacts configured keys in JSON or URL-encoded bodies. Bodies may have been cut
     * to maxBodyLength, so when parsing fails a pattern match does the job instead.
     * @param {string | undefined} text
     */
    body(text) {
      if (!bodyKeys.size || typeof text !== 'string' || !text) return text;
      const trimmed = text.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          return JSON.stringify(redactValue(JSON.parse(trimmed), bodyKeys));
        } catch {
          // truncated or not quite JSON: fall through to the pattern match
        }
      }
      return keyPattern
        ? text.replace(jsonPattern, '$1"[redacted]"').replace(formPattern, '$1$2=%5Bredacted%5D')
        : text;
    },

    /** Redacts FormData field pairs from bodySummary(). */
    fields(fields) {
      if (!bodyKeys.size || !Array.isArray(fields)) return fields;
      return fields.map(([name, value]) => [name, bodyKeys.has(lower(name)) ? REDACTED : value]);
    },

    /** A request body summary with body text and form fields redacted. */
    summary(summary) {
      if (!summary) return summary;
      const out = { ...summary };
      if (out.body !== undefined) out.body = this.body(out.body);
      if (out.fields) out.fields = this.fields(out.fields);
      return out;
    },

    /** True when a form field or body key with this name should be redacted. */
    isKey(name) {
      return !!name && bodyKeys.has(lower(name));
    },

    /** @param {unknown} value */
    object(value) {
      return bodyKeys.size ? redactValue(value, bodyKeys) : value;
    },
  };
}
